/**
 * recordMp4 — Browser video export harness for NSPP.
 *
 * Two-canvas architecture:
 *   - glCanvas: hidden WebGL2 canvas at the target export resolution.
 *     This is where setupEngine() draws the scene. The canvas is sized
 *     1×1 in CSS but has a backing buffer of (width × height) — the
 *     compositor samples the backing buffer pixels, not the CSS pixels,
 *     so the WebM is at full resolution.
 *   - captureCanvas: same-size 2D canvas. Each frame, we drawImage(glCanvas)
 *     onto it. captureStream() is attached to captureCanvas; the compositor
 *     samples this canvas and emits frames at its own pace.
 *
 * Why two canvases: glCanvas alone fails because the compositor samples
 * "what the user sees" — but glCanvas is positioned off-screen with
 * opacity:0, so the compositor samples the page background instead.
 * captureCanvas IS visible to the compositor (in normal flow) and is
 * what getUserMedia/captureStream reads.
 *
 * Frame pacing: setTimeout at the target interval (not RAF). RAF is tied
 * to the compositor's vsync — if the scene is heavy enough that rendering
 * takes longer than 33ms, RAF slows down, fewer frames are captured.
 * setTimeout runs at the wall-clock interval we want.
 *
 * Output: WebM/VP9 (what Chrome's MediaRecorder emits from captureStream).
 * The user can transcode to MP4 with ffmpeg if needed.
 */

import type { Scene } from "./sceneGraph";
import { setupEngine, frame, type Engine } from "./main";

export interface RecordOpts {
  /** Width / height of the output. Defaults to 1280×720. */
  width?: number;
  height?: number;
  /** Frames per second. Defaults to 30. */
  fps?: number;
  /** Total duration in seconds. */
  durationSec: number;
  /** Scene factory — receives (w, h) and returns a Scene. */
  sceneFactory: (w: number, h: number) => Scene;
  /** Progress callback (0..1). */
  onProgress?: (p: number) => void;
  /** Bitrate in bits/sec. Defaults to 4 Mbps. */
  bitrate?: number;
  /** Mime type for the recorder. Defaults to webm. */
  mimeType?: string;
}

export async function recordScene(opts: RecordOpts): Promise<Blob> {
  const width = opts.width ?? 1280;
  const height = opts.height ?? 720;
  const fps = opts.fps ?? 30;
  const totalFrames = Math.max(1, Math.round(opts.durationSec * fps));
  const bitrate = opts.bitrate ?? 4_000_000;
  const mimeType =
    opts.mimeType ??
    (MediaRecorder.isTypeSupported("video/webm;codecs=vp9")
      ? "video/webm;codecs=vp9"
      : "video/webm");

  // ---------- Build the offscreen rendering surface ----------
  // Two canvases stacked at the same off-screen position:
  //   - glCanvas: full-resolution WebGL2 surface where setupEngine draws.
  //   - captureCanvas: 2D surface that mirrors glCanvas per frame.
  const glCanvas = document.createElement("canvas");
  glCanvas.width = width;
  glCanvas.height = height;
  glCanvas.style.position = "fixed";
  glCanvas.style.left = "-99999px";
  glCanvas.style.top = "0";
  glCanvas.style.opacity = "0";
  glCanvas.style.pointerEvents = "none";
  document.body.appendChild(glCanvas);

  const captureCanvas = document.createElement("canvas");
  captureCanvas.width = width;
  captureCanvas.height = height;
  captureCanvas.style.position = "fixed";
  captureCanvas.style.left = "-99998px";
  captureCanvas.style.top = "0";
  captureCanvas.style.pointerEvents = "none";
  document.body.appendChild(captureCanvas);

  const ctx2d = captureCanvas.getContext("2d");
  if (!ctx2d) throw new Error("Could not create 2D capture canvas context");

  // Wait one frame so the canvases are mounted in the DOM and have real
  // clientWidth/clientHeight values (otherwise initGL sees 0).
  await new Promise<void>((r) => requestAnimationFrame(() => r()));

  // ---------- Initialize the engine on glCanvas ----------
  const engine = setupEngine(glCanvas);
  if (!engine) {
    glCanvas.remove();
    captureCanvas.remove();
    throw new Error("Failed to initialize NSPP engine on hidden canvas");
  }

  // Force the GL backbuffer to exactly the target size. setupEngine uses
  // clientWidth*renderScale which is correct for the live preview but
  // wrong for offscreen capture. We don't want DPR scaling for export.
  if (engine.ctx) {
    engine.ctx.dpr = 1;
    engine.ctx.renderScale = 1;
    engine.ctx.gl.viewport(0, 0, width, height);
    // Force FBO resize to the export dimensions.
    engine.sceneFbo = null;
    engine.scratchFbo = null;
    engine.backdropFbo = null;
    engine.velocityFbo = null;
  }

  // ---------- Wire the captureStream + MediaRecorder ----------
  const stream = captureCanvas.captureStream(fps);
  const recorder = new MediaRecorder(stream, {
    mimeType,
    videoBitsPerSecond: bitrate,
  });

  const chunks: Blob[] = [];
  recorder.ondataavailable = (e) => {
    if (e.data.size > 0) chunks.push(e.data);
  };

  const stopped = new Promise<void>((resolve) => {
    recorder.onstop = () => resolve();
  });

  recorder.start();

  // ---------- Load the scene ----------
  const scene = opts.sceneFactory(width, height);
  engine.scene = scene;
  engine.sceneName = "record";
  engine.currentScene = "record";
  for (const n of Array.from(scene.nodes.values())) {
    n.prevX = n.x;
    n.prevY = n.y;
    n.prevRotation = n.rotation;
  }

  // ---------- Render frames at wall-clock 1/fps intervals ----------
  // setTimeout-based pacing rather than RAF — see file header.
  let frameIdx = 0;
  await new Promise<void>((resolve) => {
    const tick = () => {
      if (frameIdx >= totalFrames) {
        resolve();
        return;
      }
      frame(engine);
      // Mirror glCanvas → captureCanvas for the compositor to sample.
      ctx2d.drawImage(glCanvas, 0, 0, width, height);
      frameIdx++;
      if (opts.onProgress) opts.onProgress(frameIdx / totalFrames);
      setTimeout(tick, 1000 / fps);
    };
    setTimeout(tick, 0);
  });

  // ---------- Finalize ----------
  recorder.stop();
  await stopped;
  glCanvas.remove();
  captureCanvas.remove();

  return new Blob(chunks, { type: mimeType });
}

/** Trigger a download of a Blob as a video file.
 *  Convenience for browser integration. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Return the file extension for the format a recordScene() call would produce.
 *  Used by the REC button to name the downloaded file. Always "webm" — VP9
 *  is what canvas.captureStream + MediaRecorder emit on Chromium/Safari.
 */
export function extensionForRecordOpts(_opts: RecordOpts): string {
  return "webm";
}